import { useState, useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'

interface FadeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string
  /** How eagerly to load — 'eager' for above-the-fold hero images, 'lazy' for cards below the fold */
  loading?: 'eager' | 'lazy'
}

/**
 * Drop-in <img> replacement that starts transparent and fades in once fully
 * decoded. Handles browser-cached images (where onLoad fires before mount)
 * by checking img.complete in a post-mount effect.
 *
 * The parent element should provide a placeholder background (bg-muted,
 * a gradient, etc.) that shows while the image loads.
 */
export function FadeImage({ src, loading = 'lazy', className, ...props }: FadeImageProps) {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    setLoaded(false)
    const img = imgRef.current
    if (img?.complete && img.getAttribute('src') === src) {
      setLoaded(true)
    }
  }, [src])

  return (
    <img
      ref={imgRef}
      key={src}
      src={src}
      loading={loading}
      decoding="async"
      className={cn(
        'transition-opacity duration-500',
        loaded ? 'opacity-100' : 'opacity-0',
        className,
      )}
      onLoad={() => setLoaded(true)}
      {...props}
    />
  )
}
